import React, { useState, useRef } from 'react';
import { Download, RefreshCw, UploadCloud } from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import { useRemittanceFiles, useUploadInwardRemittance } from '../../../hooks/useRemittance';
import { format } from 'date-fns';
import { Loader } from '../../../components/ui/Loader';
import { useModal } from '../../../hooks/useModal';
import { RemittanceDownloadModal } from '../../../components/RemittanceDownloadModal';
import { cn } from '../../../utils/cn';
import { useAuth } from '../../../hooks/useAuth';
import toast from 'react-hot-toast';

const pageSizeOptions = [
  { label: '10 / page', value: '10' },
  { label: '20 / page', value: '20' },
  { label: '50 / page', value: '50' },
  { label: '100 / page', value: '100' },
];

export default function InwardRemittancePage() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { open: openModal, close: closeModal, Modal } = useModal();
  const { user } = useAuth();
  const { mutate: uploadInward, isPending: isUploading } = useUploadInwardRemittance();
  const [filter, setFilter] = useState({
    fromDate: format(
      new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      'yyyy-MM-dd'
    ),
    toDate: format(new Date(), 'yyyy-MM-dd'),
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { data, isLoading, refetch } = useRemittanceFiles({
    ...filter,
    fileType: 'inward',
    page: currentPage,
    size: rowsPerPage,
  });

  const handleRefresh = () => {
    refetch();
  };

  const openDownloadModal = () => {
    openModal({
      element: <RemittanceDownloadModal onClose={closeModal} />,
      closable: true,
      className: 'max-w-xl',
    });
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('userId', user?.id?.toString() || '1584');

    uploadInward(formData, {
      onSuccess: (res) => {
        if (res.status === 'SUCCESS') {
          toast.success(res.message || 'File uploaded successfully');
          refetch();
        } else {
          toast.error(res.message || 'Upload failed');
        }
      },
      onError: (err: any) => {
        toast.error(err.response?.data?.message || 'Something went wrong');
      },
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const history = (data?.data || []).map((item, idx) => ({
    no: (currentPage - 1) * rowsPerPage + idx + 1,
    date: format(new Date(item.uploadDate), 'dd-MM-yyyy HH:mm'),
    fileName: item.fileName,
    totalOrders: item.totalRecords,
    successfulOrders: item.successCount,
    errorOrders: item.failedCount,
    fileUrl: item.fileUrl,
  }));

  const totalCount = data?.totalRecords || 0;
  const totalPages = data?.totalPages || 0;
  const paginatedHistory = history;

  return (
    <>
      <div className="min-h-screen py-6">
        <div className="mx-auto space-y-6 sm:px-4 md:px-4 lg:px-0">
          {/* Header */}
          <div className="flex flex-col md;pl-0 pl-4">
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
              Inward Remittance
            </h1>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Manage inward remittance uploads</span>
            </div>
          </div>

          {/* Upload Action Box - Premium Overhaul */}
          <div className="relative mx-4 overflow-hidden rounded-3xl border border-gray-100 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)]">
            {/* Background Decorative Element */}
            <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-teal/5 blur-3xl transition-all duration-700" />

            <div className="relative flex flex-col items-center justify-between gap-8 lg:flex-row">
              <div className="flex-1 space-y-3 text-center lg:text-left">
                <div className="inline-flex items-center gap-2 rounded-full bg-teal/10 px-3 py-1 text-xs font-bold text-teal">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-teal opacity-75"></span>
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-teal"></span>
                  </span>
                  QUICK UPLOAD
                </div>
                <h3 className="text-xl font-extrabold text-gray-900 tracking-tight">
                  Import Inward Remittance
                </h3>
                <p className="max-w-xl text-sm leading-relaxed text-gray-500">
                  Download our standardized sample file, populate it with your
                  remittance data, and upload it back for processing.
                  <span className="mt-2 block font-medium text-teal/70">
                    Ensure all mandatory fields are accurately filled.
                  </span>
                </p>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-5 shrink-0">
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept=".csv, .xlsx, .xls"
                  onChange={handleFileUpload}
                />

                {/* Download Card */}
                <div
                  onClick={downloadSample}
                  className="group relative flex items-center gap-4 rounded-2xl border border-gray-100 bg-white p-4 pr-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-teal/20 hover:shadow-md cursor-pointer active:scale-95"
                >
                  <div className="absolute right-3 top-3 rounded-md bg-green-500 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-tighter text-white">
                    XLSX
                  </div>
                  <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-green-50 transition-colors group-hover:bg-green-100">
                    <img
                      src="/excel.png"
                      alt="Excel"
                      className="h-10 w-10 object-contain drop-shadow-sm transition-transform duration-300 group-hover:scale-110"
                    />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-bold text-gray-900 group-hover:text-teal transition-colors">
                      Sample Template
                    </span>
                    <span className="text-[11px] font-medium text-gray-400">
                      Download Format
                    </span>
                  </div>
                  <div className="ml-2 flex h-8 w-8 items-center justify-center rounded-full bg-gray-50 text-gray-400 transition-all group-hover:bg-teal group-hover:text-white">
                    <Download className="h-4 w-4" />
                  </div>
                </div>

                {/* Upload Button */}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="group flex items-center gap-3 rounded-lg bg-gradient-to-br from-teal to-tealDark px-8 py-4 font-bold text-white shadow-lg shadow-teal/20 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-teal/30 active:scale-95 disabled:opacity-50"
                >
                  <div className="rounded-lg bg-white/20 p-1 transition-transform duration-500 group-hover:rotate-12">
                    {isUploading ? (
                      <RefreshCw className="h-5 w-5 text-white animate-spin" />
                    ) : (
                      <UploadCloud className="h-5 w-5 text-white" />
                    )}
                  </div>
                  <span>{isUploading ? 'Uploading...' : 'Upload File'}</span>
                </button>
              </div>
            </div>
          </div>

          {/* History Section */}
          <div className="space-y-4">
            <div className="flex justify-end pr-2 gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={openDownloadModal}
                className="text-[#006060] font-medium"
              >
                <Download className="h-4 w-4 mr-1.5" />
                Download Data
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                className="text-[#006060] font-medium"
              >
                <RefreshCw className="h-4 w-4 mr-1.5" />
                Refresh
              </Button>
            </div>

            {/* History Table Header (Desktop) */}
            <div className="hidden lg:block bg-teal text-white rounded-xl px-4 py-3 shadow-md border rounded-xl px-3 xl:px-5 py-2.5 text-xs xl:text-sm font-bold text-white shadow-md">
              <div className="grid grid-cols-[50px_160px_1fr_100px_130px_130px_120px] gap-4 items-center text-center">
                <div className="text-left pl-2">No</div>
                <div>Date and Time</div>
                <div className="text-left">File Name</div>
                <div>Orders</div>
                <div>Successful</div>
                <div>Error</div>
                <div>Action</div>
              </div>
            </div>

            {/* History List (Desktop) */}
            <div className="hidden lg:block space-y-3">
              {isLoading ? (
                <div className="flex justify-center py-20">
                  <Loader />
                </div>
              ) : history.length === 0 ? (
                <Card className="p-10 text-center text-gray-500 italic">
                  No inward remittance history found for the selected period.
                </Card>
              ) : (
                paginatedHistory.map((item, idx) => (
                  <div
                    key={idx}
                    className="bg-white border rounded-xl px-4 py-3 shadow-sm hover:shadow-md transition-all"
                  >
                    <div className="grid grid-cols-[50px_160px_1fr_100px_130px_130px_120px] gap-4 items-center text-center text-sm">
                      <div className="text-left pl-2 text-gray-500 font-medium">
                        {item.no}.
                      </div>
                      <div className="text-gray-700">{item.date}</div>
                      <div className="text-left text-gray-900 font-medium truncate pr-4">
                        {item.fileName}
                      </div>
                      <div className="font-semibold text-gray-800">
                        {item.totalOrders}
                      </div>
                      <div className="font-semibold text-green-600">
                        {item.successfulOrders}
                      </div>
                      <div className="font-semibold text-red-600">
                        {item.errorOrders}
                      </div>

                      <div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            item.fileUrl && window.open(item.fileUrl, '_blank')
                          }
                          disabled={!item.fileUrl}
                          className="text-[#006060]"
                        >
                          <Download className="h-5 w-5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-4 px-4 sm:px-4 md:px-4 lg:px-0">
              {isLoading ? (
                <div className="flex justify-center py-10">
                  <Loader />
                </div>
              ) : history.length === 0 ? (
                <Card className="p-6 text-center text-gray-500 italic">
                  No history found.
                </Card>
              ) : (
                paginatedHistory.map((item) => (
                  <Card key={item.no} className="p-4 space-y-3">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-gray-900">
                          #{item.no}
                        </span>
                        <span className="text-xs text-gray-500">
                          {item.date}
                        </span>
                      </div>
                    </div>
                    <p className="text-sm font-medium text-gray-800 line-clamp-1">
                      {item.fileName}
                    </p>
                    <div className="grid grid-cols-3 gap-2 py-2 border-y border-gray-100">
                      <div className="flex flex-col items-center">
                        <span className="text-xs text-gray-500">Orders</span>
                        <span className="font-bold">{item.totalOrders}</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <span className="text-xs text-green-600">Success</span>
                        <span className="font-bold text-green-600">
                          {item.successfulOrders}
                        </span>
                      </div>
                      <div className="flex flex-col items-center">
                        <span className="text-xs text-red-600">Error</span>
                        <span className="font-bold text-red-600">
                          {item.errorOrders}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-end pt-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 text-[#006060]"
                        onClick={() =>
                          item.fileUrl && window.open(item.fileUrl, '_blank')
                        }
                        disabled={!item.fileUrl}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download
                      </Button>
                    </div>
                  </Card>
                ))
              )}
            </div>

            {!isLoading && history.length > 0 && (
              <div className="mt-6 flex flex-wrap items-center justify-between gap-4 bg-white p-4 rounded-xl border">
                <div className="text-sm text-gray-600 font-medium">
                  Showing page {currentPage}
                  {totalCount > rowsPerPage && totalPages > 0
                    ? ` of ${totalPages}`
                    : ''}
                </div>
                <div className="flex items-center gap-3">
                  <Select
                    options={pageSizeOptions}
                    value={String(rowsPerPage)}
                    position="top"
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  />
                  <Button
                    variant="outline"
                    onClick={() => {
                      setCurrentPage((prev) => Math.max(prev - 1, 1));
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    disabled={currentPage <= 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setCurrentPage((prev) =>
                        totalCount > rowsPerPage && totalPages > 0
                          ? Math.min(prev + 1, totalPages)
                          : prev + 1
                      );
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    disabled={currentPage >= totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Modal />
    </>
  );
}

// Download sample template
function downloadSample() {
  const link = document.createElement('a');
  link.href = '/templates/inward.xlsx';
  link.download = 'inward-remittance-template.xlsx';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
